// Global Instructions Rule Applied!
//
// Single source of truth for the Configuration AREA's nav items: one entry per
// config panel, each its own menu item / tab / route. Consumed by:
//   * tabSlice.js          — generates the defaultTabs entries (addModuleTab
//                            silently no-ops without one)
//   * TabContentWrapper    — routes tab.module → ConfigSectionPage(sectionKey)
//   * iconMap.tsx          — generates MODULE_ICON_KEYS entries
//   * MainTabContainer     — hides adminOnly items from non-admins (display
//                            only; the /config/* routes enforce for real)
//
// PLAIN DATA ONLY — no JSX, no component imports — so tabSlice.js and
// iconMap.tsx can import it without cycles. The panel components live in
// CONFIG_SECTIONS (ConfigurationPage.tsx), joined via sectionKey.
//
// moduleKey doubles as the cfg.menu_items key and the route's first path
// segment (parseRoute maps module = first segment, so hrefs stay
// single-segment). Keys are 'config-' prefixed so they are globally unique
// across areas ('deal-execution', 'agent-access' already exist elsewhere) and
// hrefs never collide — an href in two areas breaks resolveUnambiguousAreaKey.
// KEEP IN SYNC with the menu seed migration
// (supabase/migrations/*_seed_configuration_menu_section.sql).

export interface ConfigModuleDef {
  /** cfg.menu_items key, tab module key, and route first-segment. */
  moduleKey: string
  /** CONFIG_SECTIONS key in ConfigurationPage.tsx (the panel to render). */
  sectionKey: string
  /** Tab + menu title. */
  title: string
  /** FA_ICONS key (iconMap.tsx) — must exist there or the icon silently
   *  renders as a generic dot. Also the cfg.menu_items.icon seed value. */
  iconKey: string
  /** Mirrors CONFIG_SECTIONS.adminOnly — drives the non-admin nav filter. */
  adminOnly: boolean
}

export const CONFIG_MODULES: ConfigModuleDef[] = [
  // DEALS & PIPELINES
  { moduleKey: 'config-pipelines', sectionKey: 'pipelines', title: 'Pipelines', iconKey: 'faColumns', adminOnly: true },
  { moduleKey: 'config-pipeline-stages', sectionKey: 'pipeline-stages', title: 'Pipeline Stages', iconKey: 'faLayerGroup', adminOnly: true },
  { moduleKey: 'config-pipeline-statuses', sectionKey: 'pipeline-statuses', title: 'Pipeline Statuses', iconKey: 'faTags', adminOnly: true },
  { moduleKey: 'config-stage-actions', sectionKey: 'stage-actions', title: 'Stage Actions', iconKey: 'faBolt', adminOnly: true },
  { moduleKey: 'config-deal-origination', sectionKey: 'deal-origination', title: 'Deal Origination', iconKey: 'faHandshake', adminOnly: true },
  { moduleKey: 'config-deal-execution', sectionKey: 'deal-execution', title: 'Deal Execution', iconKey: 'faDiagramProject', adminOnly: true },
  { moduleKey: 'config-deal-documents', sectionKey: 'deal-documents', title: 'Deal Documents', iconKey: 'faFileContract', adminOnly: true },
  // CRM & LISTS
  { moduleKey: 'config-industries', sectionKey: 'industries', title: 'Industries', iconKey: 'faIndustry', adminOnly: false },
  { moduleKey: 'config-lookup-lists', sectionKey: 'lookup-lists', title: 'Lookup Lists', iconKey: 'faList', adminOnly: true },
  { moduleKey: 'config-contact-relationships', sectionKey: 'contact-relationships', title: 'Contact Relationships', iconKey: 'faPeopleArrows', adminOnly: true },
  { moduleKey: 'config-account-owner-roles', sectionKey: 'account-owner-roles', title: 'Account Owner Roles', iconKey: 'faUsersGear', adminOnly: true },
  { moduleKey: 'config-relationship-fact-types', sectionKey: 'relationship-fact-types', title: 'Relationship Fact Types', iconKey: 'faAddressBook', adminOnly: true },
  // AGENTS & AI
  // Connect an Agent is the one item in this area that is NOT admin-only: it is
  // the connect flow every user needs, and it cannot live on the My Agents
  // screen because that screen only exists once you already have a grant.
  { moduleKey: 'config-connect-agent', sectionKey: 'connect-agent', title: 'Connect an Agent', iconKey: 'faLink', adminOnly: false },
  { moduleKey: 'config-internal-agents', sectionKey: 'internal-agents', title: 'Internal Agents', iconKey: 'faRobot', adminOnly: true },
  { moduleKey: 'config-follower-agents', sectionKey: 'follower-agents', title: 'Follower Agents', iconKey: 'faSatelliteDish', adminOnly: true },
  { moduleKey: 'config-agent-gateway', sectionKey: 'agent-gateway', title: 'Agent Gateway', iconKey: 'faPlug', adminOnly: true },
  // Renamed from "Agent Access" and moved to Operations → AGENTS in cfg
  // (owner 2026-09-02): it is view-only cross-user monitoring, not policy, and
  // "Agent Access" sitting beside "Connect an Agent" invited users to click the
  // one they cannot open. Key, route and panel are unchanged — only the label.
  { moduleKey: 'config-agent-access', sectionKey: 'agent-access', title: 'Agent Oversight', iconKey: 'faUsersGear', adminOnly: true },
  // COMMUNICATIONS
  { moduleKey: 'config-email-categories', sectionKey: 'email-categories', title: 'Email Categories', iconKey: 'faEnvelope', adminOnly: true },
  { moduleKey: 'config-interaction-tracking', sectionKey: 'interaction-tracking', title: 'Interaction Tracking', iconKey: 'faInbox', adminOnly: true },
  { moduleKey: 'config-docusign-envelopes', sectionKey: 'docusign-envelopes', title: 'DocuSign Envelopes', iconKey: 'faSignature', adminOnly: true },
  // FUNDS (fund status, owner 2026-09-04). A new category rather than filing
  // this under ACCOUNTING: a fund lifecycle catalogue is not accounting, and
  // FUNDS gives the fee-basis / day-count / late-closer lookups a home when
  // they get panels. iconKey must exist in ui_configs/iconMap.tsx or it renders
  // a generic dot with no error — faFlag, the obvious pick, is not in the map.
  { moduleKey: 'config-fund-statuses', sectionKey: 'fund-statuses', title: 'Fund Statuses', iconKey: 'faDollarSign', adminOnly: true },
  // the shapes a fund's distribution waterfall is created from (owner
  // 2026-10-12); seeded by 20270325120200_seed_config_waterfall_templates_menu
  { moduleKey: 'config-waterfall-templates', sectionKey: 'waterfall-templates', title: 'Waterfall Templates', iconKey: 'faLayerGroup', adminOnly: true },
  // the report each statement-run document prints - the v1 / v2 deck switch
  // (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md section i); seeded by
  // 20270512120100_seed_config_statement_types_menu
  { moduleKey: 'config-statement-types', sectionKey: 'statement-types', title: 'Statement Types', iconKey: 'faFileLines', adminOnly: true },
  // ACCOUNTING
  { moduleKey: 'config-gl-accounts', sectionKey: 'gl-accounts', title: 'GL Accounts', iconKey: 'faBook', adminOnly: true },
  { moduleKey: 'config-chart-templates', sectionKey: 'chart-templates', title: 'Chart Templates', iconKey: 'faFileLines', adminOnly: true },
  // USERS & ACCESS: roles, their members and the rights matrix
  // (docs/USER_RIGHTS_DESIGN.md section 7); seeded by
  // 20270513120100_seed_config_users_roles_menu
  { moduleKey: 'config-users-roles', sectionKey: 'users-roles', title: 'Users & Roles', iconKey: 'faUserShield', adminOnly: true }
]

/** moduleKey → def, for O(1) lookups in TabContentWrapper / the nav filter. */
/**
 * Root class for a Configuration panel that scrolls as a page. ConfigurationPage
 * pads its content column (px-2 sm:px-4 py-2 sm:py-4) and clips it, so a panel
 * whose root is merely `h-full overflow-y-auto` leaves the padding strips beside
 * it dead to the mouse wheel: the wheel lands on the clipped column and the page
 * does not move. This root cancels the column padding with negative margins,
 * re-adds it inside the scroll container, and lets the column's flex layout
 * size it to the whole column (flex-1 rather than h-full, so the margins are
 * included). Owner report 2026-09-09 on Follower Agents; applied to every
 * page-scrolling panel. Panels that scroll inside a bounded table keep their
 * own flex-column roots.
 */
export const CONFIG_PANEL_SCROLL_CLASS =
  'flex-1 min-h-0 overflow-y-auto -mx-2 sm:-mx-4 -my-2 sm:-my-4 px-2 sm:px-4 py-2 sm:py-4'

export const CONFIG_MODULES_BY_KEY: Record<string, ConfigModuleDef> =
  Object.fromEntries(CONFIG_MODULES.map((m) => [m.moduleKey, m]))
